import { useEffect, useState } from 'react';
import {
  Card, Button, Form, InputNumber, Radio, Tag, Steps, Result, App as AntApp
} from 'antd';
import {
  CreditCardOutlined, ThunderboltOutlined, CheckCircleOutlined,
  ArrowLeftOutlined, CalendarOutlined, TeamOutlined
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import endpoints from '../../api/endpoints';
import { paiseToRupees, formatDate } from '../../utils/format';
import { notifyError } from '../../utils/notify';
import useAuth from '../../hooks/useAuth';
import {
  openRazorpayCheckout, describeCheckoutError
} from '../../utils/razorpay';
import LoadingBlock from '../../components/LoadingBlock';
import PageHeader from '../../components/PageHeader';

// Stage 5N-8: tenant subscription renewal page.
//
// Master-only — sub-users hit the same /subscription path-tree but
// MasterOnlyRoute redirects them away, and the backend
// /api/subscription/renewal/* endpoints also enforce master via
// requireMaster.
//
// Reachable even when the subscription is expired: the Stage 5N-8
// login gate is soft for masters, NotSuspendedRoute does NOT wrap
// /subscription/*, and subscriptionEnforce is intentionally not
// mounted on /api/subscription. So an expired master can sign in,
// land here, and pay to restore access.

const STEP_QUOTE = 0;
const STEP_PAY = 1;
const STEP_DONE = 2;

function PriceRow({ label, value, emphasis = false, highlight = false }) {
  return (
    <div className={'flex items-center justify-between px-4 py-2.5 text-sm '
      + (highlight ? 'bg-brand-50 rounded-lg my-1' : '')}>
      <span className={highlight ? 'font-semibold text-ink-900' : 'text-ink-500'}>
        {label}
      </span>
      <span className={emphasis
        ? 'text-lg font-bold text-brand-700'
        : 'font-medium text-ink-900'}>
        {value}
      </span>
    </div>
  );
}

export default function RenewSubscriptionPage() {
  const { notification, modal } = AntApp.useApp();
  const { me, refreshMe } = useAuth();
  const navigate = useNavigate();

  const [bootstrapping, setBootstrapping] = useState(true);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(STEP_QUOTE);

  // Pre-load subscription so we can pre-fill cycle + minUsers.
  const [currentSub, setCurrentSub] = useState(null);
  const [minUsers, setMinUsers] = useState(1);

  const [billingCycle, setBillingCycle] = useState('monthly');
  const [userCount, setUserCount] = useState(1);
  const [quote, setQuote] = useState(null);
  const [quotedKey, setQuotedKey] = useState(null);
  const [order, setOrder] = useState(null);
  const [done, setDone] = useState(null);

  // ---- bootstrap: pull a quote at the current cycle/usercount so
  // the page lands on a populated breakdown.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const sub = await endpoints.subscription.get();
        if (cancelled) return;
        const cycle = sub.billingCycle === 'annual' ? 'annual' : 'monthly';
        const used = (sub.users && sub.users.used) || 0;
        const total = sub.currentUserCount || sub.baseUserCount || used || 1;
        const startUsers = Math.max(used || 1, total || 1);
        setBillingCycle(cycle);
        setUserCount(startUsers);
        setMinUsers(Math.max(1, used || 1));
        setCurrentSub(sub);
      } catch (err) {
        notifyError(err, { title: 'Could not load subscription' });
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const quoteKey = `${billingCycle}:${userCount}`;
  const quoteIsFresh = Boolean(quote && quotedKey === quoteKey);

  async function getQuote() {
    setBusy(true);
    try {
      const q = await endpoints.subscription.renewal.quote({
        billingCycle, userCount: Number(userCount)
      });
      setQuote(q);
      setQuotedKey(quoteKey);
      // Server-side authoritative minUsers
      if (q && q.minUsers) setMinUsers(q.minUsers);
    } catch (err) {
      setQuote(null);
      setQuotedKey(null);
      notifyError(err, { title: 'Could not fetch renewal quote' });
    } finally {
      setBusy(false);
    }
  }

  async function createOrder() {
    if (!quoteIsFresh) {
      notifyError(null, {
        title: 'Quote required',
        description: 'Please get a fresh quote before creating the order.',
        severity: 'warning'
      });
      return;
    }
    setBusy(true);
    try {
      const o = await endpoints.subscription.renewal.createOrder({
        billingCycle, userCount: Number(userCount)
      });
      setOrder(o);
      setStep(STEP_PAY);
    } catch (err) {
      notifyError(err, { title: 'Could not create renewal order' });
    } finally {
      setBusy(false);
    }
  }

  async function pay() {
    if (!order) return;
    setBusy(true);
    let signaturePayload;
    try {
      if (order.mode === 'mock') {
        // Dev-only: same path the seats addon uses. Backend's
        // razorpayService.isMockMode() returns true so any non-empty
        // signature passes verifySignature.
        signaturePayload = {
          razorpayPaymentId: 'pay_mock_renew_' + Date.now(),
          razorpayOrderId: order.razorpayOrderId,
          razorpaySignature: 'mock_signature'
        };
      } else {
        signaturePayload = await openRazorpayCheckout({
          keyId: order.razorpayKeyId,
          orderId: order.razorpayOrderId,
          amountPaise: order.amountPaise,
          currency: order.currency || 'INR',
          name: order.name || 'Glass-i',
          description: order.description || 'Glass-i Subscription Renewal',
          prefill: {
            name: me ? me.email : '',
            email: me ? me.email : '',
            contact: ''
          },
          notes: {
            tenantId: me ? me.tenantId : '',
            purpose: 'renewal',
            billingCycle,
            userCount: String(userCount)
          }
        });
      }
    } catch (checkoutErr) {
      const code = checkoutErr && checkoutErr.code;
      const description = describeCheckoutError(checkoutErr);
      setBusy(false);
      if (code === 'CHECKOUT_DISMISSED') {
        notification.info({
          message: 'Payment was cancelled',
          description: 'You can try again when ready. No charge was made.',
          placement: 'topRight', duration: 4
        });
      } else {
        modal.error({
          title: 'Razorpay checkout could not open',
          content: description, okText: 'OK'
        });
      }
      return;
    }

    try {
      const res = await endpoints.subscription.renewal.verifyPayment({
        razorpayOrderId: signaturePayload.razorpayOrderId,
        razorpayPaymentId: signaturePayload.razorpayPaymentId,
        razorpaySignature: signaturePayload.razorpaySignature
      });
      setDone(res);
      setStep(STEP_DONE);
      notification.success({
        message: 'Subscription renewed',
        description: `Invoice ${res.invoice ? res.invoice.invoiceNumber : ''} · access until ${
          res.subscription ? formatDate(res.subscription.currentPeriodEnd) : ''
        }`,
        placement: 'topRight', duration: 6
      });
      // Refresh /me so SubscriptionBanner stops nagging.
      try { await refreshMe(); } catch (_e) { /* noop */ }
    } catch (err) {
      notifyError(err, { title: 'Payment could not be verified' });
    } finally {
      setBusy(false);
    }
  }

  if (bootstrapping) return <LoadingBlock tip="Loading subscription…" />;

  return (
    <div>
      <PageHeader
        title="Renew subscription"
        subtitle="Extend your Glass-i subscription with another billing period."
        icon={<CreditCardOutlined />}
        actions={
          <Link to="/subscription">
            <Button icon={<ArrowLeftOutlined />}>Back to subscription</Button>
          </Link>
        }
      />

      <Steps
        current={step}
        size="small"
        items={[
          { title: 'Choose plan' },
          { title: order && order.mode === 'mock' ? 'Pay (mock)' : 'Pay' },
          { title: 'Done' }
        ]}
        className="!my-6"
      />

      {currentSub ? (
        <Card size="small" title="Current subscription" className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-ink-500 m-0 mb-1">Plan</p>
              <p className="font-semibold text-ink-900 m-0">
                {currentSub.planCode} <span className="text-ink-400 text-xs">({currentSub.billingCycle})</span>
              </p>
            </div>
            <div>
              <p className="text-ink-500 m-0 mb-1">Status</p>
              <Tag color={
                currentSub.effectiveStatus === 'active'    ? 'green' :
                currentSub.effectiveStatus === 'reminder'  ? 'gold'  :
                currentSub.effectiveStatus === 'grace'     ? 'orange':
                currentSub.effectiveStatus === 'suspended' ? 'red'   : 'default'
              }>{currentSub.effectiveStatus || '—'}</Tag>
            </div>
            <div>
              <p className="text-ink-500 m-0 mb-1">Period ends</p>
              <p className="font-semibold text-ink-900 m-0">
                {currentSub.currentPeriodEnd
                  ? formatDate(currentSub.currentPeriodEnd) : '—'}
              </p>
            </div>
          </div>
        </Card>
      ) : null}

      {step === STEP_QUOTE ? (
        <Card size="small" title="Choose your renewal">
          <Form layout="vertical" disabled={busy} size="large">
            <Form.Item label={<span className="text-sm font-semibold text-ink-800">Billing cycle</span>}>
              <Radio.Group
                value={billingCycle}
                onChange={(e) => {
                  setBillingCycle(e.target.value);
                  setQuote(null); setQuotedKey(null);
                }}
                buttonStyle="solid"
              >
                <Radio.Button value="monthly"><CalendarOutlined /> Monthly</Radio.Button>
                <Radio.Button value="annual"><CalendarOutlined /> Annual</Radio.Button>
              </Radio.Group>
            </Form.Item>

            <Form.Item label={<span className="text-sm font-semibold text-ink-800">Users</span>}>
              <div className="flex items-center gap-3 flex-wrap">
                <InputNumber
                  min={minUsers}
                  max={1000}
                  value={userCount}
                  onChange={(v) => {
                    const n = Number(v);
                    setUserCount(Number.isFinite(n) ? n : minUsers);
                    setQuote(null); setQuotedKey(null);
                  }}
                  prefix={<TeamOutlined className="text-ink-400" />}
                  className="!w-44"
                />
                <span className="text-xs text-ink-500">
                  Minimum: <strong>{minUsers}</strong> (current active users)
                </span>
              </div>
            </Form.Item>

            <div className="flex items-center gap-2 flex-wrap">
              <Button
                onClick={getQuote}
                disabled={busy || userCount < minUsers}
                loading={busy && !quoteIsFresh}
                icon={<ThunderboltOutlined />}
              >
                Get quote
              </Button>
              <Button
                type="primary"
                onClick={createOrder}
                disabled={!quoteIsFresh || busy}
                loading={busy && quoteIsFresh}
                icon={<CreditCardOutlined />}
              >
                Pay & Renew
              </Button>
              {quote && !quoteIsFresh ? (
                <span className="text-xs text-amber-600 ml-1">
                  Selection changed — get a fresh quote.
                </span>
              ) : null}
            </div>
          </Form>

          {quote && quote.quote ? (
            <div className="mt-6 rounded-xl border border-surface-200 bg-surface-50 overflow-hidden">
              <div className="px-4 py-2.5 bg-white border-b border-surface-200">
                <p className="text-xs uppercase tracking-wider text-ink-500 font-semibold m-0">
                  Renewal quote
                </p>
              </div>
              <div className="divide-y divide-surface-200">
                <PriceRow label="Plan cycle" value={quote.quote.billingCycle} />
                <PriceRow label="Users" value={quote.quote.userCount} />
                <PriceRow label="Base" value={paiseToRupees(quote.quote.basePaise)} />
                {Number(quote.quote.discountPaise) > 0 ? (
                  <PriceRow label="Discount" value={`− ${paiseToRupees(quote.quote.discountPaise)}`} />
                ) : null}
                <PriceRow label="Taxable" value={paiseToRupees(quote.quote.taxablePaise)} />
                <PriceRow
                  label={`GST (${Number(quote.quote.gstRatePercent).toFixed(2)}%)`}
                  value={paiseToRupees(quote.quote.gstPaise)}
                />
                {Number(quote.quote.freeMonths) > 0 ? (
                  <PriceRow
                    label="Free months"
                    value={`+ ${quote.quote.freeMonths} month(s)`}
                  />
                ) : null}
                <PriceRow
                  label="New period"
                  value={`${formatDate(quote.quote.periodStart)} → ${formatDate(quote.quote.periodEnd)}`}
                />
              </div>
              <div className="border-t border-surface-200 p-2">
                <PriceRow
                  label="Total payable"
                  value={paiseToRupees(quote.quote.totalPaise)}
                  emphasis
                  highlight
                />
              </div>
            </div>
          ) : null}
        </Card>
      ) : null}

      {step === STEP_PAY ? (
        <Card size="small" title="Confirm payment">
          <div className="rounded-xl border border-surface-200 bg-surface-50 overflow-hidden">
            <div className="divide-y divide-surface-200">
              <PriceRow label="Order id" value={
                <code className="text-xs">{order && order.razorpayOrderId}</code>
              } />
              <PriceRow label="Plan cycle" value={(order && order.billingCycle) || ''} />
              <PriceRow label="Users" value={(order && order.userCount) || ''} />
            </div>
            <div className="border-t border-surface-200 p-2">
              <PriceRow
                label="Amount"
                value={paiseToRupees(order && order.amountPaise)}
                emphasis highlight
              />
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 mt-5 flex-wrap">
            <Button
              onClick={() => setStep(STEP_QUOTE)}
              disabled={busy}
              icon={<ArrowLeftOutlined />}
            >
              Back
            </Button>
            <Button
              type="primary"
              onClick={pay}
              loading={busy}
              icon={<CreditCardOutlined />}
            >
              {order && order.mode === 'mock'
                ? `Pay (mock) ${paiseToRupees(order.amountPaise)}`
                : `Pay ${paiseToRupees(order && order.amountPaise)}`}
            </Button>
          </div>
        </Card>
      ) : null}

      {step === STEP_DONE ? (
        <Card size="small">
          <Result
            status="success"
            icon={<CheckCircleOutlined className="!text-emerald-500" />}
            title={<span className="text-xl font-bold text-ink-900">Subscription renewed</span>}
            subTitle={done && done.subscription ? (
              <span className="text-ink-500">
                New period ends {formatDate(done.subscription.currentPeriodEnd)}.
                {done.invoice ? ` Invoice ${done.invoice.invoiceNumber}.` : ''}
              </span>
            ) : null}
          />
          <div className="flex justify-center gap-2">
            <Button type="primary" onClick={() => navigate('/subscription')}>Back to subscription</Button>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
